export default function BlogContent() {
  return (
    <article className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            You've built a solid reputation in Vancouver's construction market. Your crew is reliable, your project management is tight, 
            and your clients trust you. But when that big commercial contract comes across your desk—the high-rise in Burnaby, the complex 
            hillside development in Coquitlam—you hesitate.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>The question isn't whether you can build it. The question is: Can you document it?</strong>
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Technical Barrier to Growth</h2>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For many growing construction firms in the Lower Mainland, the jump from residential projects to major commercial contracts 
            isn't about crew capacity or equipment—it's about technical documentation. Major developers and general contractors require:
          </p>

          <ul className="space-y-3 mb-8">
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
              <span className="text-gray-700"><strong>Professional AutoCAD shop drawings</strong> that meet engineering standards</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
              <span className="text-gray-700"><strong>Accurate Bluebeam takeoffs</strong> for material coordination</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
              <span className="text-gray-700"><strong>Complex formwork layouts</strong> for architectural concrete</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
              <span className="text-gray-700"><strong>Fast turnaround times</strong> to keep projects on schedule</span>
            </li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Without this technical capability, you're locked out of the most profitable projects in Vancouver's construction market.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-r-lg">
            <p className="text-lg text-gray-800 italic">
              "We had the crew and the equipment to handle a 12-story tower in Surrey, but we didn't have anyone who could produce 
              the technical drawings fast enough. We lost the bid to a firm half our size—because they had the documentation ready."
            </p>
            <p className="text-sm text-gray-600 mt-3">— Project Manager, Mid-Sized Vancouver Contractor</p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The $100k+ Hiring Problem</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The traditional solution is to hire a Senior Detailer or CAD Specialist. But here's what that actually costs in 2026:
          </p>

          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Full-Time Senior Detailer Annual Cost:</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-700">Base Salary</span>
                <span className="font-semibold text-gray-900">$85,000 - $110,000</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-700">Benefits & Payroll Taxes (CPP, EI, EHT)</span>
                <span className="font-semibold text-gray-900">$12,000 - $18,000</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-700">AutoCAD & Bluebeam Licenses</span>
                <span className="font-semibold text-gray-900">$2,500 - $3,500</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-700">Workstation & Hardware</span>
                <span className="font-semibold text-gray-900">$3,000 - $5,000</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-gray-700">Training & Onboarding (2-4 weeks)</span>
                <span className="font-semibold text-gray-900">$5,000 - $8,000</span>
              </div>
              <div className="flex justify-between items-center pt-3">
                <span className="text-lg font-bold text-gray-900">Total Annual Cost:</span>
                <span className="text-2xl font-bold text-teal-600">$107,500 - $144,500</span>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            And that's assuming you have consistent work to keep them busy year-round. During slow periods, you're still paying 
            that fixed cost—even when there's no revenue coming in.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Dinari Designs Solution: Your Instant Engineering Department</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Here's the strategic advantage that's allowing smaller Vancouver firms to compete for major contracts: 
            <strong> outsourcing the Senior Detailer role to a specialized contractor like Dinari Designs.</strong>
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Instead of hiring a full-time employee, you gain access to 20+ years of civil engineering expertise and professional 
            AutoCAD/Bluebeam capabilities—on demand, with zero overhead.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">How It Works:</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border-2 border-teal-100 rounded-xl p-6 hover:border-teal-300 transition-colors duration-300">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-flashlight-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">1. Fast Quote Turnaround</h4>
              <p className="text-gray-700">
                Send us your project specs. We provide a detailed quote within 24-48 hours—no waiting, no delays.
              </p>
            </div>

            <div className="bg-white border-2 border-teal-100 rounded-xl p-6 hover:border-teal-300 transition-colors duration-300">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-pencil-ruler-2-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">2. Professional Documentation</h4>
              <p className="text-gray-700">
                We produce engineer-grade AutoCAD drawings and Bluebeam takeoffs that meet developer requirements.
              </p>
            </div>

            <div className="bg-white border-2 border-teal-100 rounded-xl p-6 hover:border-teal-300 transition-colors duration-300">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">3. Variable Cost Structure</h4>
              <p className="text-gray-700">
                You only pay for the projects you win. No fixed salaries, no benefits, no software licenses to maintain.
              </p>
            </div>

            <div className="bg-white border-2 border-teal-100 rounded-xl p-6 hover:border-teal-300 transition-colors duration-300">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">4. Instant Scalability</h4>
              <p className="text-gray-700">
                Handle multiple projects simultaneously without hiring additional staff or expanding your office space.
              </p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Real-World Example: 5-Person Team, 50-Person Capability</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A growing contractor in New Westminster recently partnered with Dinari Designs to bid on a complex hillside development 
            in Coquitlam—a project that would have been impossible without professional technical documentation.
          </p>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">The Results:</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700"><strong>Won the $2.8M contract</strong> against larger, more established firms</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700"><strong>Received complete AutoCAD layouts</strong> within 5 business days</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700"><strong>Avoided $110k+ in annual hiring costs</strong> for a full-time detailer</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700"><strong>Positioned themselves for future high-rise projects</strong> in Burnaby and Surrey</span>
              </li>
            </ul>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            By outsourcing the technical bottleneck, this small team was able to present themselves as a fully-capable contractor—
            without the overhead burden of a large in-house engineering department.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What This Means for Your Business</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you're a growing construction firm in Vancouver, Burnaby, Surrey, or anywhere in the Lower Mainland, partnering with 
            Dinari Designs gives you:
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start bg-white border border-gray-200 rounded-lg p-4">
              <i className="ri-building-4-line text-2xl text-teal-600 mr-4 mt-1 flex-shrink-0"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Access to High-Value Projects</h4>
                <p className="text-gray-700">Bid on high-rise towers, complex hillside developments, and architectural concrete projects</p>
              </div>
            </div>

            <div className="flex items-start bg-white border border-gray-200 rounded-lg p-4">
              <i className="ri-percent-line text-2xl text-teal-600 mr-4 mt-1 flex-shrink-0"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Reduced Overhead Costs</h4>
                <p className="text-gray-700">Convert fixed employee costs into variable project expenses</p>
              </div>
            </div>

            <div className="flex items-start bg-white border border-gray-200 rounded-lg p-4">
              <i className="ri-speed-line text-2xl text-teal-600 mr-4 mt-1 flex-shrink-0"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Faster Project Turnaround</h4>
                <p className="text-gray-700">Meet aggressive developer timelines with professional documentation support</p>
              </div>
            </div>

            <div className="flex items-start bg-white border border-gray-200 rounded-lg p-4">
              <i className="ri-shield-check-line text-2xl text-teal-600 mr-4 mt-1 flex-shrink-0"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Zero Market Risk</h4>
                <p className="text-gray-700">No fixed costs during slow periods—you only pay when you have active projects</p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Bottom Line</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            You don't need to hire a $100k+ Senior Detailer to compete for major contracts in Vancouver's construction market. 
            You need a strategic partner who can provide that expertise on demand—without the overhead, without the risk, and 
            without the delays.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>Dinari Designs is that partner.</strong> We handle the technical bottleneck so you can focus on what you do best: 
            building exceptional projects and growing your business.
          </p>

          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 text-center mt-12">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Lean Out Your Overhead?
            </h3>
            <p className="text-teal-100 mb-6 text-lg">
              Let's discuss how Dinari Designs can help you win bigger contracts without the burden of full-time hiring costs.
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              Get a Quote
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <a
              href="/knowledge-hub"
              className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Knowledge Hub
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
